import React from 'react';
import PortalService from '../../../services/portal/portal.service';
import { usePortalResource } from '../usePortalResource';
import PortalSection, { Card, EmptyState } from '../PortalSection';
import type { PortalMedication } from '../../../dtos/portal/portal.dto';
import { formatPortalDate } from '../format';

const MedicationList: React.FC<{ title: string; items: PortalMedication[] }> = ({ title, items }) => (
  <section aria-label={title}>
    <h2 className="mb-2 text-base font-semibold text-slate-900">{title}</h2>
    {items.length === 0 ? (
      <EmptyState>None.</EmptyState>
    ) : (
      <ul className="flex flex-col gap-3">
        {items.map((med, index) => (
          <li key={`${med.name}-${index}`}>
            <Card>
              <p className="font-medium text-slate-900">
                {med.name}
                {med.prn && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-800">As needed</span>}
              </p>
              <p className="text-sm text-slate-700">
                {[med.dose, med.route, med.frequency].filter(Boolean).join(' · ')}
              </p>
              <p className="text-sm text-slate-700">
                {med.startDate ? `Started ${formatPortalDate(med.startDate, false)}` : ''}
                {med.endDate ? ` · Ended ${formatPortalDate(med.endDate, false)}` : ''}
              </p>
            </Card>
          </li>
        ))}
      </ul>
    )}
  </section>
);

/** Active and inactive medications, read-only. */
const MedicationsScreen: React.FC<{ patientId: string }> = ({ patientId }) => {
  const { data, loading, error } = usePortalResource(PortalService.getMedications, patientId);
  const active = data?.medications.filter((m) => m.status === 'ACTIVE') ?? [];
  const inactive = data?.medications.filter((m) => m.status !== 'ACTIVE') ?? [];
  return (
    <PortalSection title="Medications" loading={loading} error={error}>
      {data && (
        <div className="flex flex-col gap-5">
          <MedicationList title="Current medications" items={active} />
          <MedicationList title="Past medications" items={inactive} />
        </div>
      )}
    </PortalSection>
  );
};

export default MedicationsScreen;
